import { Button, Checkbox, Input, Select, Text, Stack, VStack } from '@genial/components';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box } from 'native-base';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ActivityIndicator, NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import { z } from 'zod';

import { useToastCreator } from '../../../../../../../../../../hooks/useToastCreator';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../../../../../routes/types';
import { usePrivatePensionAssetController } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import {
  PrivatePensionPortabilityInfo,
  PrivatePensionPortabilityRentabilityType,
  PrivatePensionPortabilityTransferType,
  PrivatePensionTaxSystem,
} from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';
import { PrivatePensionModalContainer } from '../PrivatePensionModalContainer';
import { PrivatePensionModalHeader } from '../PrivatePensionModalHeader';

const CustomMoneyMask = (value: number | string) => {
  if (typeof value === 'string') {
    return Utils.Mask.InputMoney(value);
  }

  return Utils.Mask.InputMoney(value * 100);
};

export const PrivatePensionPortabilityModal = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route =
    useRoute<RouteProp<SignedInRootStackParamList, 'CustomerDetailPrivatePensionPortability'>>();
  const { index, portability, offer, accountId, accountNumber } = route.params;
  const { showToast } = useToastCreator('portabilityToast');
  const [maxValue, setMaxValue] = useState<number>(Number.MAX_SAFE_INTEGER);
  const [certificateLoading, setCertificateLoading] = useState<boolean>(false);
  const [susepLoading, setSusepLoading] = useState<boolean>(false);

  const portabilitySchema = useMemo(
    () =>
      z.object({
        certificateNumber: z
          .string({
            required_error: 'Número do certificado é obrigatório',
          })
          .nonempty('Número do certificado é obrigatório')
          .refine((value) => !value.match(/^\D$/), {
            message: 'Número do certificado deve conter apenas números',
          })
          .transform((value) => value.replace(/[^\d]/g, '')),
        susepNumber: z
          .string({
            required_error: 'Número SUSEP é obrigatório',
          })
          .nonempty('Número SUSEP é obrigatório')
          .refine((value) => value.replace(/[^\d]/g, '').length === 17, {
            message: 'Número SUSEP deve conter 17 números',
          })
          .transform((value) => value.replace(/[^\d]/g, '')),
        susepNumberAutofilled: z.boolean().optional(),
        clientInsuranceCompany: z
          .number({
            required_error: 'Seguradora cedente é obrigatória',
          })
          .nonnegative('Seguradora cedente é obrigatória'),
        clientInsuranceCompanyDocument: z
          .string({
            required_error: 'CNPJ é obrigatório',
          })
          .nonempty('CNPJ é obrigatório')
          .refine((value) => Utils.Validate.validateCnpj(value), {
            message: 'CNPJ inválido',
          }),
        fundDocument: z
          .string({
            required_error: 'CNPJ é obrigatório',
          })
          .nonempty('CNPJ é obrigatório')
          .refine((value) => Utils.Validate.validateCnpj(value), {
            message: 'CNPJ inválido',
          }),
        planName: z
          .string({
            required_error: 'Nome do plano é obrigatório',
          })
          .nonempty('Nome do plano é obrigatório'),
        rentabilityType: z.nativeEnum(PrivatePensionPortabilityRentabilityType, {
          required_error: 'Retratabilidade é obrigatória',
        }),
        rentabilityTypeAutofilled: z.boolean().optional(),
        taxSystem: z.nativeEnum(PrivatePensionTaxSystem, {
          required_error: 'Regime tributário é obrigatório',
        }),
        taxSystemAutofilled: z.boolean().optional(),
        transferType: z.nativeEnum(PrivatePensionPortabilityTransferType, {
          required_error: 'Tipo de transferência é obrigatório',
        }),
        value: z.preprocess(
          (value) => (typeof value === 'string' ? value : Utils.Mask.Money(value as number)),
          z
            .string({
              required_error: 'Valor é obrigatório',
            })
            .refine((value) => Utils.Unmask.Money(value) >= 100, {
              message: 'Valor deve ser maior ou igual a R$ 100,00',
            })
            .transform((value) => Utils.Unmask.Money(value))
        ),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxValue]
  );

  const {
    fundOptions,
    insuranceOptions,
    addPortability,
    updatePortability,
    findPortabilityDataFromCertificateNumber,
    findPortabilityDataFromSusepNumber,
  } = usePrivatePensionAssetController(offer, accountId, accountNumber);

  const formEdit = useForm<z.infer<typeof portabilitySchema>>({
    resolver: zodResolver(portabilitySchema),
    mode: 'all',
    reValidateMode: 'onSubmit',
    defaultValues: {
      certificateNumber: portability?.certificateNumber || undefined,
      susepNumber: portability?.susepNumber || undefined,
      susepNumberAutofilled: portability?.susepNumberAutofilled || undefined,
      clientInsuranceCompany: portability?.clientInsuranceCompany || undefined,
      clientInsuranceCompanyDocument: portability?.clientInsuranceCompanyDocument || undefined,
      fundDocument: portability?.fundDocument || undefined,
      planName: portability?.planName || undefined,
      rentabilityType: portability?.rentabilityType || undefined,
      rentabilityTypeAutofilled: portability?.rentabilityTypeAutofilled || undefined,
      taxSystem: portability?.taxSystem || undefined,
      taxSystemAutofilled: portability?.taxSystemAutofilled || undefined,
      transferType: portability?.transferType || undefined,
      value: portability?.value || 0,
    },
  });

  const {
    setValue,
    formState: { isValid, errors },
    getValues,
    watch,
    handleSubmit,
  } = formEdit;
  const title = index === undefined ? 'Incluir Portabilidade' : 'Editar Portabilidade';

  const formClientInsuranceCompany = watch('clientInsuranceCompany');
  const formFundDocument = watch('fundDocument');
  const formPlanName = watch('planName');
  const formRentabilityType = watch('rentabilityType');
  const formTaxSystem = watch('taxSystem');
  const formTransferType = watch('transferType');
  const formSusepNumberAutofilled = watch('susepNumberAutofilled');
  const formRentabilityTypeAutofilled = watch('rentabilityTypeAutofilled');
  const formTaxSystemAutofilled = watch('taxSystemAutofilled');
  const fundListDisabled = fundOptions.length === 1;
  const clientInsuranceCompanyDisabled = formClientInsuranceCompany !== undefined;
  const shouldDisableValue =
    formTransferType === PrivatePensionPortabilityTransferType.Total &&
    maxValue !== Number.MAX_SAFE_INTEGER;

  // used only to trigger validation
  const onSubmit = handleSubmit(() => {});

  const handleCancel = () => {
    navigation.goBack();
  };

  const handleSavePortability = () => {
    const normalizedValues = portabilitySchema.parse(getValues());

    if (index === undefined) {
      addPortability(normalizedValues as unknown as PrivatePensionPortabilityInfo);
    } else {
      updatePortability(index, normalizedValues as unknown as PrivatePensionPortabilityInfo);
    }

    navigation.goBack();
  };

  const handleTransferTypeChange = (newValue: string) => {
    setValue('transferType', Number(newValue) as PrivatePensionPortabilityTransferType, {
      shouldValidate: true,
    });

    if (
      Number(newValue) === PrivatePensionPortabilityTransferType.Total &&
      maxValue !== Number.MAX_SAFE_INTEGER
    ) {
      setValue('value', maxValue, {
        shouldValidate: true,
      });
    }
  };

  const handleRentabilityTypeChange = (newValue: string) => {
    setValue('rentabilityType', newValue as PrivatePensionPortabilityRentabilityType, {
      shouldValidate: true,
    });
  };

  const handleTaxSystemChange = (newValue: string) => {
    setValue('taxSystem', Number(newValue) as PrivatePensionTaxSystem, {
      shouldValidate: true,
    });
  };

  const handleFundChange = (newValue: string) => {
    setValue('fundDocument', newValue, {
      shouldValidate: true,
    });
  };

  const handleInsuranceChange = (newValue: string) => {
    setValue('clientInsuranceCompany', Number(newValue), {
      shouldValidate: true,
    });
  };

  const clearFieldsWhenDataIsNotFound = (shouldClearSusepNumber: boolean = false) => {
    setValue('fundDocument', undefined as any, { shouldValidate: true });

    if (shouldClearSusepNumber) {
      setValue('susepNumber', undefined as any);
    }

    setValue('susepNumberAutofilled', false as any);
    setValue('clientInsuranceCompany', undefined as any);
    setValue('clientInsuranceCompanyDocument', undefined as any);
    setValue('planName', '');
    setValue('rentabilityType', undefined as any);
    setValue('rentabilityTypeAutofilled', false as any);
    setValue('taxSystem', undefined as any);
    setValue('taxSystemAutofilled', false as any);
    setValue('transferType', undefined as any);
    setValue('value', 0 as any);
    setMaxValue(Number.MAX_SAFE_INTEGER);
  };

  const handleCertificateBlur = (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
    const certificateNumber = event.nativeEvent.text;

    if (certificateNumber.trim() === '') {
      return;
    }

    setCertificateLoading(true);

    findPortabilityDataFromCertificateNumber(certificateNumber).then((data) => {
      setCertificateLoading(false);
      const setterOptions = {
        shouldValidate: true,
      };

      if (!data) {
        clearFieldsWhenDataIsNotFound(true);

        showToast(
          'error',
          'Certificado não encontrado',
          'Número de certificado não encontrado na base da Genial, favor preencher o número do SUSEP'
        );
      }

      if (data?.susepNumber) {
        setValue('susepNumber', data.susepNumber, setterOptions);
      }

      setValue('susepNumberAutofilled', data?.susepNumberAutofilled || false, setterOptions);

      if (data?.value) {
        setMaxValue(data.value);
        setValue('value', data.value, setterOptions);
        setValue('transferType', PrivatePensionPortabilityTransferType.Total, setterOptions);
      } else {
        setMaxValue(Number.MAX_SAFE_INTEGER);
      }

      if (data?.clientInsuranceCompany) {
        setValue('clientInsuranceCompany', data.clientInsuranceCompany, setterOptions);
      }

      if (data?.clientInsuranceCompanyDocument) {
        setValue(
          'clientInsuranceCompanyDocument',
          data.clientInsuranceCompanyDocument,
          setterOptions
        );
      }

      if (data?.fundDocument) {
        setValue('fundDocument', data.fundDocument, setterOptions);
      }

      if (data?.taxSystem) {
        setValue('taxSystem', data.taxSystem, setterOptions);
      }

      setValue('taxSystemAutofilled', data?.taxSystemAutofilled || false, setterOptions);

      if (data?.rentabilityType) {
        setValue('rentabilityType', data.rentabilityType, setterOptions);
      }

      setValue(
        'rentabilityTypeAutofilled',
        data?.rentabilityTypeAutofilled || false,
        setterOptions
      );

      if (data?.planName) {
        setValue('planName', data.planName, setterOptions);
      }
    });
  };

  const handleSusepNumberBlur = (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
    const susepNumber = event.nativeEvent.text;

    if (susepNumber.trim() === '') {
      return;
    }

    setSusepLoading(true);

    findPortabilityDataFromSusepNumber(susepNumber).then((data) => {
      setSusepLoading(false);
      const setterOptions = {
        shouldValidate: true,
      };

      if (!data) {
        clearFieldsWhenDataIsNotFound();

        showToast(
          'error',
          'SUSEP não encontrado',
          'Número do SUSEP não encontrado na base da Genial, favor buscar por outro SUSEP para prosseguir'
        );
      }

      if (data?.clientInsuranceCompany) {
        setValue('clientInsuranceCompany', data.clientInsuranceCompany, setterOptions);
      }

      if (data?.clientInsuranceCompanyDocument) {
        setValue(
          'clientInsuranceCompanyDocument',
          data.clientInsuranceCompanyDocument,
          setterOptions
        );
      }

      if (data?.fundDocument) {
        setValue('fundDocument', data.fundDocument, setterOptions);
      }

      if (data?.planName) {
        setValue('planName', data.planName, setterOptions);
      }
    });
  };

  useEffect(() => {
    if (index !== undefined) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PrivatePensionModalContainer>
      <VStack space="stack.nano">
        <PrivatePensionModalHeader title={title} onClose={handleCancel} />
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Numero do certificado
            </Text>
            <Input
              name="certificateNumber"
              placeholder="Digite o numero do certificado"
              variant="filled"
              autoComplete="off"
              control={formEdit.control}
              error={errors.certificateNumber}
              hasShadow={false}
              onBlur={handleCertificateBlur}
              inputRightElement={
                certificateLoading ? (
                  <Box marginRight="inset.nano">
                    <ActivityIndicator size="small" />
                  </Box>
                ) : undefined
              }
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              SUSEP
            </Text>
            <Input
              name="susepNumber"
              placeholder="Número SUSEP"
              variant="filled"
              autoComplete="off"
              control={formEdit.control}
              error={errors.susepNumber}
              hasShadow={false}
              mask={Utils.Mask.Susep}
              onBlur={handleSusepNumberBlur}
              isDisabled={formSusepNumberAutofilled}
              inputRightElement={
                susepLoading ? (
                  <Box marginRight="inset.nano">
                    <ActivityIndicator size="small" />
                  </Box>
                ) : undefined
              }
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Seguradora cedente
            </Text>
            <Select
              placeholder="Selecione a seguradora"
              options={insuranceOptions}
              selectedValue={formClientInsuranceCompany?.toString()}
              onValueChange={handleInsuranceChange}
              disabled={clientInsuranceCompanyDisabled}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              CNPJ
            </Text>
            <Input
              name="clientInsuranceCompanyDocument"
              placeholder="Digite o CNPJ da seguradora"
              variant="filled"
              autoComplete="off"
              mask={Utils.Mask.Cnpj}
              control={formEdit.control}
              error={errors.clientInsuranceCompanyDocument}
              hasShadow={false}
              isDisabled={clientInsuranceCompanyDisabled}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Fundo
            </Text>
            <Select
              placeholder="Selecione o fundo"
              selectedValue={formFundDocument?.toString()}
              options={fundOptions}
              onValueChange={handleFundChange}
              disabled={fundListDisabled}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Nome do plano
            </Text>
            <Input
              name="planName"
              placeholder="Digite o nome do plano"
              variant="filled"
              autoComplete="off"
              value={formPlanName}
              control={formEdit.control}
              error={errors.planName}
              hasShadow={false}
              isDisabled={fundListDisabled}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Retratabilidade
            </Text>
            <Checkbox
              testId="private-pension-portability-rentability-type"
              onChange={handleRentabilityTypeChange}
              hasError={!!errors.rentabilityType}
              options={[
                {
                  label: 'Retratável',
                  value: PrivatePensionPortabilityRentabilityType.FixedReturnRate,
                  disabled: formRentabilityTypeAutofilled,
                },
                {
                  label: 'Irretratável',
                  value: PrivatePensionPortabilityRentabilityType.VariableReturnRate,
                  disabled: formRentabilityTypeAutofilled,
                },
              ]}
              selectedValues={formRentabilityType ? [formRentabilityType] : []}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Regime tributário
            </Text>
            <Checkbox
              testId="private-pension-portability-tax-system"
              onChange={handleTaxSystemChange}
              hasError={!!errors.taxSystem}
              options={[
                {
                  label: 'Progressiva',
                  value: PrivatePensionTaxSystem.Progressive.toString(),
                  disabled: formTaxSystemAutofilled,
                },
                {
                  label: 'Regressiva',
                  value: PrivatePensionTaxSystem.Regressive.toString(),
                  disabled: formTaxSystemAutofilled,
                },
              ]}
              selectedValues={formTaxSystem ? [formTaxSystem.toString()] : []}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Tipo de transferência
            </Text>
            <Checkbox
              testId="private-pension-portability-transfer-type"
              onChange={handleTransferTypeChange}
              hasError={!!errors.transferType}
              options={[
                { label: 'Total', value: PrivatePensionPortabilityTransferType.Total.toString() },
                {
                  label: 'Parcial',
                  value: PrivatePensionPortabilityTransferType.Partial.toString(),
                },
              ]}
              selectedValues={formTransferType ? [formTransferType.toString()] : []}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Valor
            </Text>
            <Input
              name="value"
              variant="filled"
              autoComplete="off"
              placeholder="Digite o valor"
              isDisabled={shouldDisableValue}
              mask={CustomMoneyMask}
              control={formEdit.control}
              error={errors.value}
              hasShadow={false}
            />
          </VStack>
        </Stack>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          width="100%"
          justifyContent={{ base: 'center', md: 'flex-end' }}
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          space="stack.nano">
          <Button
            variant="secondary"
            onPress={handleCancel}
            testID="private-pension-portability-modal-cancel-button">
            Cancelar
          </Button>
          <Button
            isDisabled={!isValid}
            testID="private-pension-portability-modal-confirm-button"
            onPress={handleSavePortability}>
            Confirmar
          </Button>
        </Stack>
      </VStack>
    </PrivatePensionModalContainer>
  );
};
